import React, { useState, useEffect } from 'react';
import styles from './NewsCarousel.module.css';

// Free RSS to JSON converter service
const RSS_TO_JSON_API = 'https://api.rss2json.com/v1/api.json';

// Financial RSS feeds (free and public)
const NEWS_FEEDS = [
  'https://feeds.finance.yahoo.com/rss/2.0/headline', // Yahoo Finance
  'https://www.investing.com/rss/news.rss', // Investing.com
  'https://www.marketwatch.com/rss/realtimeheadlines', // MarketWatch
];

export default function NewsCarousel() {
  const [newsData, setNewsData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [error, setError] = useState(null);

  // Fetch live news from RSS feeds
  const fetchLiveNews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch from multiple RSS sources
      let allNews = [];
      
      for (const feed of NEWS_FEEDS) {
        try {
          const response = await fetch(`${RSS_TO_JSON_API}?rss_url=${encodeURIComponent(feed)}&api_key=free&count=10`);
          
          if (response.ok) {
            const data = await response.json();
            
            if (data.status === 'ok' && data.items) {
              const processedNews = data.items.map((item, index) => ({
                id: `${feed}-${index}-${Date.now()}`,
                title: item.title,
                description: item.description ? item.description.replace(/<[^>]*>/g, '').substring(0, 200) + '...' : '',
                url: item.link,
                urlToImage: item.thumbnail || item.enclosure?.link || null,
                publishedAt: item.pubDate,
                source: { name: getSourceName(feed) },
                category: getCategoryFromFeed(feed)
              }));
              
              allNews = [...allNews, ...processedNews];
            }
          }
        } catch (feedError) {
          console.warn(`Failed to fetch from ${feed}:`, feedError);
        }
      }
      
      if (allNews.length > 0) {
        // Sort by date and take the latest 12 articles
        const sortedNews = allNews
          .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
          .slice(0, 12);
        
        setNewsData(sortedNews);
        setLastUpdate(new Date());
      } else {
        // Fallback to static data if APIs fail
        setNewsData(getFallbackNews());
        setError('Using sample news - Live feeds temporarily unavailable');
      }
      
    } catch (error) {
      console.error('Failed to fetch news:', error);
      setNewsData(getFallbackNews());
      setError('Using sample news - Live feeds temporarily unavailable');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and auto-refresh every 10 minutes
  useEffect(() => {
    fetchLiveNews();
    
    // Auto-refresh every 10 minutes
    const refreshInterval = setInterval(() => {
      fetchLiveNews();
    }, 10 * 60 * 1000); // 10 minutes
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Auto-rotate carousel every 6 seconds
  useEffect(() => {
    if (!isPaused && newsData.length > 0) {
      const carouselInterval = setInterval(() => {
        setCurrentIndex(prev => 
          prev >= newsData.length - 3 ? 0 : prev + 1
        );
      }, 6000); // 6 seconds per rotation

      return () => clearInterval(carouselInterval);
    }
  }, [isPaused, newsData.length]);

  // Helper functions
  const getSourceName = (feedUrl) => {
    if (feedUrl.includes('yahoo')) return 'Yahoo Finance';
    if (feedUrl.includes('investing')) return 'Investing.com';
    if (feedUrl.includes('marketwatch')) return 'MarketWatch';
    return 'Financial News';
  };

  const getCategoryFromFeed = (feedUrl) => {
    if (feedUrl.includes('yahoo')) return 'Markets';
    if (feedUrl.includes('investing')) return 'Global';
    if (feedUrl.includes('marketwatch')) return 'Trading';
    return 'Finance';
  };

  const getFallbackNews = () => [
    {
      id: 'fallback-1',
      title: "Markets Show Resilience Amid Global Economic Uncertainty",
      description: "Financial markets continue to demonstrate strength as investors navigate through complex global economic conditions and policy changes.",
      url: "https://finance.yahoo.com/news/",
      urlToImage: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=200&fit=crop",
      publishedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
      source: { name: "Financial Markets" },
      category: "Markets"
    },
    {
      id: 'fallback-2',
      title: "Currency Markets React to Central Bank Policy Signals",
      description: "Major currency pairs experience volatility as central banks worldwide provide guidance on future monetary policy directions.",
      url: "https://www.investing.com/news/forex-news",
      urlToImage: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&h=200&fit=crop",
      publishedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      source: { name: "Forex News" },
      category: "Forex"
    },
    {
      id: 'fallback-3',
      title: "Technology Sector Drives Market Innovation Forward",
      description: "Leading technology companies continue to shape market trends through breakthrough innovations and strategic investments.",
      url: "https://www.investing.com/news/stock-market-news",
      urlToImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop",
      publishedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      source: { name: "Tech Markets" },
      category: "Technology"
    }
  ];

  const handleNewsClick = (url) => {
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleNext = () => {
    setCurrentIndex(prev => 
      prev >= newsData.length - 3 ? 0 : prev + 1
    );
  };

  const handlePrev = () => {
    setCurrentIndex(prev => 
      prev <= 0 ? newsData.length - 3 : prev - 1
    );
  };

  const formatTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    } catch {
      return 'Recently';
    }
  };

  if (loading && newsData.length === 0) {
    return (
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h2>Financial <span>News Feed</span></h2>
            <p>Loading latest market developments...</p>
          </div>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Fetching live financial news...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Financial <span>News Feed</span></h2>
          <p>Stay updated with the latest market developments</p>
          {error && (
            <div className={styles.errorIndicator}>
              <span>⚠️</span> {error}
            </div>
          )}
          {!error && (
            <div className={styles.liveIndicator}>
              <span className={styles.liveDot}></span>
              Live Updates Active
            </div>
          )}
        </div>

        <div className={styles.newsWidget}>
          <div 
            className={styles.newsCarousel}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Refresh Button */}
            <button 
              className={styles.refreshButton}
              onClick={fetchLiveNews}
              disabled={loading}
              title="Refresh news"
            >
              <span className={loading ? styles.spinning : ''}>🔄</span>
            </button>

            {/* Navigation Buttons */}
            <button className={styles.navButton} onClick={handlePrev}>
              <span>‹</span>
            </button>
            <button className={`${styles.navButton} ${styles.navNext}`} onClick={handleNext}>
              <span>›</span>
            </button>

            {/* News Items */}
            <div className={styles.newsContainer}>
              {newsData.slice(currentIndex, currentIndex + 3).map((article, index) => (
                <div 
                  key={article.id}
                  className={styles.newsCard}
                  onClick={() => handleNewsClick(article.url)}
                >
                  <div className={styles.newsImageContainer}>
                    {article.urlToImage && (
                      <img 
                        src={article.urlToImage} 
                        alt={article.title}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    )}
                    <div className={styles.imagePlaceholder}>
                      <span>📰</span>
                    </div>
                    <div className={styles.newsCategory}>
                      {article.category}
                    </div>
                  </div>
                  
                  <div className={styles.newsContent}>
                    <div className={styles.newsMetadata}>
                      <span className={styles.newsSource}>
                        {article.source?.name}
                      </span>
                      <span className={styles.newsDot}>•</span>
                      <span className={styles.newsTime}>
                        {formatTimeAgo(article.publishedAt)}
                      </span>
                    </div>
                    
                    <h3 className={styles.newsTitle}>
                      {article.title}
                    </h3>
                    
                    <p className={styles.newsDescription}>
                      {article.description}
                    </p>
                    
                    <div className={styles.readMore}>
                      Read Full Article →
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Progress Indicators */}
            <div className={styles.indicators}>
              {Array.from({ length: Math.max(1, newsData.length - 2) }, (_, i) => (
                <button
                  key={i}
                  className={`${styles.indicator} ${i === currentIndex ? styles.activeIndicator : ''}`}
                  onClick={() => setCurrentIndex(i)}
                />
              ))}
            </div>
          </div>

          {/* Live Updates Status */}
          <div className={styles.liveStatus}>
            <div className={styles.liveIndicator}>
              <span className={styles.liveDot}></span>
              {error ? 'Demo Mode' : 'Live News Feed'}
            </div>
            <span className={styles.updateTime}>
              Last updated: {lastUpdate.toLocaleTimeString()}
            </span>
            <button className={styles.manualRefresh} onClick={fetchLiveNews}>
              Manual Refresh
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
